/**
 * Which of the three operations workspaces (UMED, Scheduling, Finance) the signed-in person can open, for the sidebar's
 * workspace switcher.
 *
 * The answer is the server's workspace index (GET /emerge/workspaces) -- the same list, and the same `available` gate
 * (ratified capability AND the eMerge operator allowlist), that the portal's top bar uses. The client never works it out
 * from a role name. Until the index answers, or if it fails, only the current workspace is listed: the person is in it,
 * and nothing else is offered on a guess. Each workspace still enforces itself when opened.
 *
 * One read per page (a short-lived shared promise), not one per render. The shared read belongs to ONE identity -- the
 * signed-in user, and the user being impersonated when there is one (verification of 2026-10-14, F3): a sign-out, a
 * different sign-in or the start or end of an impersonation never reuses another identity's answer, because a sign-out
 * does not reload the page. Signed out (a shell mounted alone, as in a test) it reads nothing, like the top bar.
 */
import { useEffect, useState } from 'react';
import { useOptionalUser } from '../../contexts/UserContext';
import { workspacesApi, type Workspace, type WorkspaceKey } from '../../services/workspacesApi';

export type OpsWorkspaceKey = Extract<WorkspaceKey, 'UMED' | 'SCHEDULING' | 'FINANCE'>;
export const OPS_WORKSPACES: readonly OpsWorkspaceKey[] = ['UMED', 'SCHEDULING', 'FINANCE'];

export interface OpsWorkspace { key: OpsWorkspaceKey; label: string; path: string }

/** Where each one lives and what it is called, when the index has not said (the current workspace before it answers). */
export const OPS_FALLBACK: Record<OpsWorkspaceKey, OpsWorkspace> = {
  UMED: { key: 'UMED', label: 'UMED', path: '/portal/umed' },
  SCHEDULING: { key: 'SCHEDULING', label: 'Scheduling', path: '/portal/scheduling' },
  FINANCE: { key: 'FINANCE', label: 'Finance', path: '/portal/finance' },
};

const isOps = (k: string): k is OpsWorkspaceKey => (OPS_WORKSPACES as readonly string[]).includes(k);

const TTL_MS = 30_000;
let shared: { identity: string; at: number; read: Promise<Workspace[] | null> } | null = null;

function readIndex(identity: string): Promise<Workspace[] | null> {
  const now = Date.now();
  if (shared && shared.identity === identity && now - shared.at < TTL_MS) return shared.read;
  const read = Promise.resolve()
    .then(() => workspacesApi.index())
    .then((r) => (r && r.success && r.data && Array.isArray(r.data.workspaces) ? r.data.workspaces : null))
    .catch(() => null);
  shared = { identity, at: now, read };
  // A failed read is not kept: the next page asks again.
  read.then((w) => { if (w === null && shared && shared.read === read) shared = null; });
  return read;
}

/** Test seam: forget the shared read. */
export function resetOpsWorkspacesCache(): void { shared = null; }

/** Pure: the ops workspaces the index offers as available, in UMED, Scheduling, Finance order; the current one always kept. */
export function opsWorkspacesFrom(index: Workspace[] | null, current: OpsWorkspaceKey): OpsWorkspace[] {
  if (!index) return [OPS_FALLBACK[current]];
  const offered = new Map<OpsWorkspaceKey, OpsWorkspace>();
  for (const w of index) {
    if (w && w.available && isOps(w.key)) offered.set(w.key, { key: w.key, label: w.label || OPS_FALLBACK[w.key].label, path: w.path || OPS_FALLBACK[w.key].path });
  }
  if (!offered.has(current)) offered.set(current, OPS_FALLBACK[current]);
  return OPS_WORKSPACES.filter((k) => offered.has(k)).map((k) => offered.get(k) as OpsWorkspace);
}

export function useOpsWorkspaces(current: OpsWorkspaceKey): OpsWorkspace[] {
  const session = useOptionalUser() as { isAuthenticated?: boolean; user?: { id?: string } | null; impersonating?: { targetUserId?: string } | null } | null | undefined;
  const signedIn = Boolean(session && session.isAuthenticated);
  // Who is asking: the signed-in user, and the impersonated user when there is one.
  const identity = signedIn ? `${(session && session.user && session.user.id) || '?'}|${(session && session.impersonating && session.impersonating.targetUserId) || ''}` : '';
  const [answer, setAnswer] = useState<{ identity: string; index: Workspace[] | null }>({ identity: '', index: null });
  useEffect(() => {
    if (!identity) { setAnswer({ identity: '', index: null }); return undefined; }
    let live = true;
    readIndex(identity).then((w) => { if (live) setAnswer({ identity, index: w }); });
    return () => { live = false; };
  }, [identity]);
  // An answer for another identity is never shown, even for the render before the new read lands.
  return opsWorkspacesFrom(answer.identity === identity ? answer.index : null, current);
}
